import React, {useMemo, useState, useEffect} from "react";
import { useNavigate } from "react-router-dom"
import { getCurrentUser } from '../utils/auth'
import { BsEye, BsEyeSlash } from 'react-icons/bs'
import './ModalUserEdit.css'
import './RegisterForm.css'

const normalizeDescription = (value) => String(value || '').trim().toLowerCase();
const isEmployeeOrCustomerAdmin = (description) => {
    const normalized = normalizeDescription(description);
    return normalized === 'employee' || normalized === 'customer admin';
};
const isCreatorDescription = (description) => normalizeDescription(description) === 'creator';

export const RegisterForm = (props) => {
    const tokenPayload = getCurrentUser() || {};
    const tokenUserType = Number(tokenPayload.user_type);
    const tokenCustomerId = Number(tokenPayload.customer ?? tokenPayload.customer_id ?? 0);
    const userScopes = tokenPayload.scopes || [];
    const hasReadPermission = userScopes.some((scope) => scope.startsWith('read:userData'));
    const hasCreateGlobalUser = userScopes.includes('create:userData');
    const hasCreateScopedUser = userScopes.some((scope) => scope.startsWith('create:userData') && scope !== 'create:userData');
    const hasCreatePermission = hasCreateGlobalUser || hasCreateScopedUser;
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userType, setUserType] = useState(0);
    const [allUserTypes, setAllUserTypes] = useState([]);
    const [customerList, setCustomerList] = useState([]);
    const [assignedCustomerId, setAssignedCustomerId] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');

    const creatorTypeFromToken = tokenPayload.user_type_description || tokenPayload.description || '';
    const currentUserTypeDescription = useMemo(() => {
        const matched = allUserTypes.find((item) => Number(item.id) === tokenUserType);
        return matched?.description || creatorTypeFromToken;
    }, [allUserTypes, tokenUserType, creatorTypeFromToken]);

    const canAssignCreatorType = isCreatorDescription(currentUserTypeDescription);

    const constrainedByCustomerScope = hasCreateScopedUser && !hasCreateGlobalUser;

    const userTypeOptions = useMemo(() => {
        if (!Array.isArray(allUserTypes)) {
            return [];
        }

        return allUserTypes.filter((typeOption) => {
            if (constrainedByCustomerScope) {
                return isEmployeeOrCustomerAdmin(typeOption.description);
            }

            if (hasCreateGlobalUser && !canAssignCreatorType && isCreatorDescription(typeOption.description)) {
                return false;
            }

            return true;
        });
    }, [allUserTypes, constrainedByCustomerScope, hasCreateGlobalUser, canAssignCreatorType]);

    const customerOptions = useMemo(() => {
        if (!Array.isArray(customerList)) {
            return [];
        }

        if (constrainedByCustomerScope && tokenCustomerId > 0) {
            return customerList.filter((customer) => Number(customer.id) === tokenCustomerId);
        }

        return customerList;
    }, [customerList, constrainedByCustomerScope, tokenCustomerId]);

    useEffect(() => {
        if (!window.sessionStorage.getItem("auth") || !hasReadPermission) {
            navigate('/unauthorized')
            return;
        }

        if (!hasCreatePermission) {
            navigate('/unauthorized')
            return;
        }

        fetch(process.env.REACT_APP_API_URL_BASE + '/user_types/',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => res.json())
            .then(data => {
                const formattedOptions = data.map(item => ({ description: item.description, id: item.user_type_id }));
                setAllUserTypes(formattedOptions)
            })
            .catch(error => console.error(error));

        fetch(process.env.REACT_APP_API_URL_BASE + '/customers/view',
            {
                method: 'GET',
                headers: {
                    'machine':'2',
                    'vh-key':'7df7f81e-aaff-444c-a01c-8caeaeab3e61',
                },
            })
            .then(res => res.json())
            .then(data => {
                const formattedOptions = data.map(item => ({ description: item.customer_name, id: item.id }));
                setCustomerList(formattedOptions)
            })
            .catch(error => console.error(error));
    }, [navigate, hasReadPermission, hasCreatePermission, tokenUserType]);

    useEffect(() => {
        if (userTypeOptions.length === 0) {
            setUserType(0);
            return;
        }

        const isCurrentSelectionValid = userTypeOptions.some((option) => Number(option.id) === Number(userType));
        if (!isCurrentSelectionValid) {
            setUserType(Number(userTypeOptions[0].id));
        }
    }, [userTypeOptions, userType]);

    useEffect(() => {
        if (customerOptions.length === 0) {
            setAssignedCustomerId(0);
            return;
        }

        if (constrainedByCustomerScope && tokenCustomerId > 0) {
            setAssignedCustomerId(tokenCustomerId);
            return;
        }

        const isCurrentSelectionValid = customerOptions.some((option) => Number(option.id) === Number(assignedCustomerId));
        if (!isCurrentSelectionValid) {
            setAssignedCustomerId(Number(customerOptions[0].id));
        }
    }, [customerOptions, assignedCustomerId, constrainedByCustomerScope, tokenCustomerId]);

    function addUserAddress(user_id) {
        // add a blank address to the user
        const address = {
            street_1: '',
            street_2: '',
            city: '',
            st: '',
            zip: '',
            country: 'United States',
            address_type: 1, // 1 = Home, 2 = Work, 3 = Shipping, 4 = Billing, 5 = Facility, 6 = Corporate Office
            web_user: user_id
        };
        const payload = JSON.stringify(address);
        console.log(payload)
        console.log(address)
        fetch(process.env.REACT_APP_API_URL_BASE + '/user_addresses/', {
            method: 'POST',
            body: payload,
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(res => {
            if (!res.ok) {
                console.error("Error adding address:", res.statusText);
            }
        }).catch(error => {
            console.error("Error adding address:", error);
        });
    }

    function addUserPhone(user_id) {
        // add a blank phone to the user
        const phone = {
            phone: '',
            phone_type: 1, // 1 = Mobile, 2 = Home, 3 = Work, 4 = Facility, 5 = Corporate
            web_user: user_id
        };
        const payload = JSON.stringify(phone);
        console.log(payload)
        console.log(phone)
        fetch(process.env.REACT_APP_API_URL_BASE + '/user_phones/', {
            method: 'POST',
            body: payload,
            headers: {
                'Content-Type': 'application/json'
            }
        }).then(res => {
            if (!res.ok) {
                console.error("Error adding phone to user:", res.statusText);
            }
        }).catch(error => {
            console.error("Error adding phone:", error);
        });
    }

    const handleSubmit = (e) => {
        e.preventDefault();
        setError(null)
        let success = true
        let errorText = ''
        if (password === passwordConfirm) {
            var payload_obj = {
                first_name: firstName,
                last_name: lastName,
                email: email,
                password: password,
                username: username,
                scopes: '',
                user_type: userType
            }

            if (constrainedByCustomerScope && tokenCustomerId > 0) {
                payload_obj.customer_id = tokenCustomerId
            }
            else if (hasCreateGlobalUser && assignedCustomerId > 0) {
                payload_obj.customer_id = assignedCustomerId
            }
            console.log(payload_obj)
            const payload = JSON.stringify(payload_obj);
            try {
                fetch(process.env.REACT_APP_API_URL_BASE + '/users/',{
                method: 'POST',
                body: payload,
                headers:{
                    'Content-Type': 'application/json'
                    }
                }).then(res => {
                        console.log(res)
                        if (!res.ok){
                            errorText = "Error: " + res.status + ' - '
                            success = false
                            return res.json()
                        }
                        else {
                            success = true
                            return res.json()
                        }
                    }
                ).then(data => {
                    if (!success) {
                        for (const err in data.errors){
                        for (const msg in data.errors[err]) errorText += data.errors[err][msg]
                        }
                        console.log(errorText)
                        setError(errorText)
                    }
                    else {
                        console.log(data)
                        addUserAddress(data.user_id)
                        addUserPhone(data.user_id)
                        console.log("User created successfully:", data);
                        setUsername('')
                        setEmail('')
                        setFirstName('')
                        setLastName('')
                        setPassword('')
                        setPasswordConfirm('')
                        setShowPassword(false)
                        if (userTypeOptions.length > 0) {
                            setUserType(Number(userTypeOptions[0].id))
                        }
                        if (constrainedByCustomerScope && tokenCustomerId > 0) {
                            setAssignedCustomerId(tokenCustomerId)
                        }
                        else if (customerOptions.length > 0) {
                            setAssignedCustomerId(Number(customerOptions[0].id))
                        }
                        setError('Registered Successfully!')
                        navigate('/users')
                    }

                })
                .catch(error => {
                    success = false
                    console.error(error)
                });
            }
            catch (error) {
                console.error(error);
                setError('Error Registering - Check your information and try again: ' + error)
            }
        }
        else {
            setError("Passwords do not match")
        }

    }

    const validatePasswords = (e) => {
        setPasswordConfirm(e.target.value)
        if (password !== e.target.value){
            setError('Password does not match')
        }
        else {
            setError(null)
        }
    }

    function changeUserType(e) {
        setUserType(Number(e.target.value))
    }

    function changeCustomer(e) {
        setAssignedCustomerId(Number(e.target.value))
    }

    function showUserType() {
        if (!hasCreatePermission) {
            return (
                <></>
            )
        }
        else {
            return (
                <>
                    <div className='form-group'>
                    <label htmlFor='user_type'>Type</label>
                    <select name="user_type" value={userType} onChange={changeUserType}>
                        {userTypeOptions.map ((user_type) => (
                            <option key={user_type.id} value={user_type.id}>
                                {user_type.description}
                            </option>
                        ))}
                    </select>
                    </div>
                </>
            )
        }


    }

    function showCustomerList() {
        if (hasCreatePermission) {
            return (
                <>
                    <div className='form-group'>
                    <label htmlFor='customers'>Customer</label>
                    <select name="customers" value={assignedCustomerId} onChange={changeCustomer} disabled={constrainedByCustomerScope}>
                        {customerOptions.map ((customer) => (
                            <option
                                key={customer.id}
                                value={customer.id}
                            >
                                {customer.description}
                            </option>
                        ))}
                    </select>
                    </div>
                </>
            )
        }
        else {
            return (
                <></>
            )
        }


    }

    return (
        <div className='register-user-page'>
            <div className='modal-edit register-user-panel'>
                <form className='register-user-form' onSubmit={handleSubmit}>
                    <div className='user-card-grid'>
                        <section className='user-card'>
                            <div className='user-card-header'>
                                <h3>New User Details</h3>
                            </div>
                            <div className='user-fields-grid two-col'>
                                <div className='form-group'>
                                    <label htmlFor='firstName'>First Name*</label>
                                    <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} type='text' placeholder='First Name' id='firstName' name='firstName'/>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='lastName'>Last Name*</label>
                                    <input required value={lastName} onChange={(e) => setLastName(e.target.value)} type='text' placeholder='Last Name' id='lastName' name='lastName'/>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='username'>Username*</label>
                                    <input required value={username} onChange={(e) => setUsername(e.target.value)} type='text' placeholder='Username' id='username' name='username'/>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='email'>Email*</label>
                                    <input required value={email} onChange={(e) => setEmail(e.target.value)} type='email' placeholder='email@email.com' id='email' name='email'/>
                                </div>
                                {showUserType()}
                                {showCustomerList()}
                            </div>
                        </section>

                        <section className='user-card'>
                            <h3>Security</h3>
                            <div className='user-fields-grid'>
                                <div className='form-group'>
                                    <label htmlFor='password'>Password*</label>
                                    <div className='register-password-field-wrap'>
                                        <input required value={password} onChange={(e) => setPassword(e.target.value)} type={showPassword ? 'text' : 'password'} placeholder='*********' id='password' name='password'/>
                                        <button
                                            type='button'
                                            className='register-password-toggle'
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            title={showPassword ? 'Hide password' : 'Show password'}
                                        >
                                            {showPassword ? <BsEyeSlash /> : <BsEye />}
                                        </button>
                                    </div>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='passwordConfirm'>Confirm Password*</label>
                                    <input required value={passwordConfirm} onChange={validatePasswords} type={showPassword ? 'text' : 'password'} placeholder='*********' id='passwordConfirm' name='passwordConfirm'/>
                                </div>
                            </div>
                        </section>
                    </div>

                    {!!error && (
                        <div className={error === 'Registered Successfully!' ? 'register-status success' : 'error'}>{error}</div>
                    )}

                    <div className='user-actions-row'>
                        <button type='button' className='btn btn-cancel' onClick={() => navigate('/users')}>Cancel</button>
                        <button className='btn' type='submit'>Register</button>
                    </div>
                </form>
            </div>
        </div>
    )
}

export default RegisterForm;


